import { maybe } from '@/features/details/utils';
import { Experience, ExperienceStatus } from '@/webapi/use-experience-api';
import { ExpVersion, SummaryResponse } from '@/webapi/use-analytics-api';
import { metricsByKind } from '@/utils/experience-utils';
import { MetricKind } from '@/webapi/models';

export function isAtLeast5HoursOld(startDate: Date) {
  return (
    Math.abs(new Date().getTime() - startDate.getTime()) / (1000 * 60 * 60) > 5
  );
}

export function isValid(startDate: Date) {
  return startDate.toString() !== `Invalid Date`;
}

export function isNextVersionDataAvailable(
  experience: Experience,
  currVersion?: number,
) {
  if (!experience.resetStatsAtPublish) {
    return true;
  }
  const currentVersion = Math.max(0, currVersion || 0);
  return currentVersion > (experience.maxVersionAtPublish || 0);
}

export function shouldDisplayChartsGraphs(
  data: any[],
  experience: Experience,
  version?: ExpVersion,
) {
  const hasData = data?.length > 0;
  if (!hasData) {
    return false;
  }
  if (!version?.isCurrent) {
    return true;
  }

  if (maybe(() => isAtLeast5HoursOld(new Date(experience.metricsStartAt)))) {
    return true;
  }

  return isNextVersionDataAvailable(experience, version?.version);
}

export function shouldDisplaySummaryMetrics(
  data: SummaryResponse,
  experience?: Experience,
  version?: ExpVersion,
) {
  const hasData =
    data !== undefined &&
    maybe(() => !!data?.variants?.find((x) => x?.metrics?.length > 0));
  if (!hasData) {
    return false;
  }
  if (!version?.isCurrent) {
    return true;
  }

  if (maybe(() => isAtLeast5HoursOld(new Date(experience.metricsStartAt)))) {
    return true;
  }

  return isNextVersionDataAvailable(experience, data?.version);
}

export function shouldHideListSummary(experience: Experience) {
  const metrics = maybe(() => metricsByKind(experience, MetricKind.SESSIONS));
  const hasSessions =
    metrics?.control > 50 || !!metrics?.variations?.find((m) => m.value > 50);
  if (!hasSessions) {
    return true;
  }

  if (maybe(() => isAtLeast5HoursOld(new Date(experience.metricsStartAt)))) {
    return false;
  }

  return !isNextVersionDataAvailable(experience, experience.maxVersion);
}

export function isWaitingForStartScheduled(
  experience: Experience,
  variants: any[],
): boolean {
  const isPublished = experience?.status === ExperienceStatus.PUBLISHED;
  const hasScheduleType = experience?.schedule?.type !== undefined;
  const isScheduleActive = experience?.isScheduleActive;

  if (!isPublished || !hasScheduleType || isScheduleActive) {
    return false;
  }

  const hasSessions = maybe(
    () =>
      Array.isArray(variants) &&
      variants.some(
        (variant) =>
          Array.isArray(variant?.metrics) &&
          variant.metrics.some(
            (m: any) =>
              m?.name === MetricKind.SESSIONS &&
              Number.isInteger(m.value) &&
              m.value > 0,
          ),
      ),
  );

  return !hasSessions;
}

export function isNotEnoughSessions(minSessions: number, variants: any[]) {
  if (!Array.isArray(variants) || variants.length === 0) {
    return false;
  }

  return variants.some((variant) => {
    const metric = variant?.metrics?.find(
      (m: any) => m?.name === MetricKind.SESSIONS,
    );

    if (!metric || !Number.isInteger(metric.value)) {
      return true;
    }

    return metric.value < minSessions;
  });
}
